import type { ClientMandateItem } from "@repo/shared/types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useConfirm } from "@/hooks/use-confirm";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { FollowupSelectionDialog, type FollowupTarget } from "./followup-selection-dialog";
import { T } from "./mandates-section.i18n";

const pct = (value: number | null) => (value == null ? "—" : `${value.toFixed(1)}%`);

type MandatesSectionProps = {
  clientId: string;
  mandates: ClientMandateItem[];
};

export function MandatesSection({ clientId, mandates }: MandatesSectionProps) {
  const t = useT(T);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [followupTarget, setFollowupTarget] = useState<FollowupTarget | null>(null);
  const { confirm, ConfirmationDialog } = useConfirm();

  const showFollowupError = (message: string) => {
    void confirm({
      title: t.error,
      description: message,
      confirmText: t.ok,
      variant: "destructive",
      type: "alert",
    });
  };

  // The mandate's accounts that resolve to client accounts — what the dialog
  // pre-ticks. A mandate naming no known account has nothing to follow up.
  const followupAccountIds = (mandate: ClientMandateItem) =>
    mandate.accounts.map((a) => a.accountId).filter((id): id is string => !!id);

  // Guard only — the tab trigger itself is hidden when the client has no mandates.
  if (mandates.length === 0) return null;

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-medium">{t.mandates}</h3>
        <p className="text-sm text-muted-foreground">{t.mandatesDescription}</p>
      </div>

      <div className="space-y-2">
        {mandates.map((mandate) => {
          const isExpanded = expanded.has(mandate.id);
          const isFixed = mandate.mode === "fixed";
          return (
            <div key={mandate.id} className="border rounded-lg">
              {/* biome-ignore lint/a11y/useSemanticElements: div with role="button" needed due to nested interactive elements */}
              <div
                role="button"
                tabIndex={0}
                className={cn(
                  "flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50",
                  isExpanded && "border-b",
                )}
                onClick={() => toggleExpanded(mandate.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleExpanded(mandate.id);
                  }
                }}
              >
                <button
                  type="button"
                  className="p-1 hover:bg-muted rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(mandate.id);
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{mandate.name}</span>
                    {/* Defensive per-status badge — the internal list is active-only server-side. */}
                    <StatusBadge status={mandate.status}>
                      {mandate.status === "pending" ? t.pending : t.active}
                    </StatusBadge>
                    {mandate.vendor && <Badge variant="outline">{mandate.vendor}</Badge>}
                    {mandate.goal && (
                      <Badge variant="outline" title={mandate.goal.name ?? undefined}>
                        {mandate.goal.name}
                      </Badge>
                    )}
                  </div>
                  {mandate.externalId && (
                    <div className="text-xs text-muted-foreground font-mono">
                      {mandate.externalId}
                    </div>
                  )}
                </div>

                {mandate.status === "active" &&
                  (followupAccountIds(mandate).length === 0 ? (
                    // None of the bank's account ids is a known client account:
                    // nothing to seed. The span carries the hover — a disabled
                    // button has pointer-events-none, so its own tooltip cannot.
                    <div className="w-28 shrink-0 flex justify-end">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex">
                            <Button variant="outline" size="sm" disabled>
                              {t.followup}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">{t.followupNoAccounts}</TooltipContent>
                      </Tooltip>
                    </div>
                  ) : (
                    <div className="w-28 shrink-0 flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFollowupTarget({
                            kind: "mandate",
                            mandateId: mandate.id,
                            accountIds: followupAccountIds(mandate),
                            goalFlowInstanceId: mandate.goal?.flowInstanceId ?? null,
                          });
                        }}
                      >
                        {t.followup}
                      </Button>
                    </div>
                  ))}

                <div className="text-right shrink-0">
                  <div className="font-medium">{isFixed ? t.modeFixed : t.modeMinMax}</div>
                  <div className="text-xs text-muted-foreground">
                    {mandate.lines.length}{" "}
                    {mandate.lines.length === 1 ? t.instrument : t.instruments}
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="p-3 space-y-3 bg-muted/20">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    <div className="flex gap-2">
                      <span className="text-muted-foreground w-28 shrink-0">{t.externalId}</span>
                      <span className="font-mono">{mandate.externalId ?? t.notSet}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-muted-foreground w-28 shrink-0">{t.mode}</span>
                      <span>{isFixed ? t.modeFixed : t.modeMinMax}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-muted-foreground w-28 shrink-0">{t.accounts}</span>
                      <span>
                        {mandate.accounts.length === 0
                          ? t.notSet
                          : mandate.accounts
                              .map((a) => (a.name ? `${a.name} (${a.externalId})` : a.externalId))
                              .join(" · ")}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-muted-foreground w-28 shrink-0">{t.linkedGoal}</span>
                      <span>{mandate.goal?.name ?? t.notSet}</span>
                    </div>
                    {mandate.vendor && (
                      <div className="flex gap-2">
                        <span className="text-muted-foreground w-28 shrink-0">{t.vendor}</span>
                        <span>{mandate.vendor}</span>
                      </div>
                    )}
                  </div>

                  <Table variant="data">
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t.isin}</TableHead>
                        <TableHead>{t.name}</TableHead>
                        {isFixed ? (
                          <TableHead className="text-right">{t.weight}</TableHead>
                        ) : (
                          <>
                            <TableHead className="text-right">{t.minimum}</TableHead>
                            <TableHead className="text-right">{t.default}</TableHead>
                            <TableHead className="text-right">{t.maximum}</TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mandate.lines.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            {line.isin ?? t.notSet}
                          </TableCell>
                          <TableCell>{line.name ?? t.notSet}</TableCell>
                          {isFixed ? (
                            <TableCell className="text-right">{pct(line.weightPct)}</TableCell>
                          ) : (
                            <>
                              <TableCell className="text-right">{pct(line.minPct)}</TableCell>
                              <TableCell className="text-right">{pct(line.defaultPct)}</TableCell>
                              <TableCell className="text-right">{pct(line.maxPct)}</TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmationDialog />
      <FollowupSelectionDialog
        clientId={clientId}
        target={followupTarget}
        onClose={() => setFollowupTarget(null)}
        onError={showFollowupError}
      />
    </div>
  );
}
